import { ConfigProvider, Table } from "antd";
import { cloneElement, forwardRef, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import styles from "./ResizableTable.module.css";

const MIN_WIDTH = 48;
const MAX_WIDTH = 5000;
const storageKey = (tableKey) => `admin:table-column-widths:v1:${tableKey}`;
const validWidth = (width) => Number.isFinite(width) && width > 0 && width <= MAX_WIDTH;
const clampWidth = (width) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(width)));
const stopPropagation = (event) => event.stopPropagation();

const readWidths = (tableKey) => {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey(tableKey)));
    if (!saved || typeof saved !== "object" || Array.isArray(saved)) return {};
    return Object.fromEntries(Object.entries(saved).filter(([, width]) => validWidth(width)));
  } catch {
    return {};
  }
};

// Identity follows the field/key rather than its position, including grouped columns.
const identifyColumns = (columns, parent = "") => columns.map((column, index) => {
  const identity = column.key != null
    ? ["key", column.key]
    : column.dataIndex != null
      ? ["field", column.dataIndex]
      : typeof column.title === "string"
        ? ["title", column.title]
        : ["index", index];
  const columnKey = `${parent}/${JSON.stringify(identity)}`;
  return {
    ...column,
    columnKey,
    ...(column.children ? { children: identifyColumns(column.children, columnKey) } : {}),
  };
});

const HeaderCell = forwardRef(({ children, resizeColumn, className, ...props }, ref) => (
  <th
    {...props}
    ref={ref}
    aria-label={props["aria-label"] ?? resizeColumn?.headerLabel}
    className={[className, resizeColumn && styles.HeaderCell].filter(Boolean).join(" ")}
    data-resize-column={resizeColumn?.key}
  >
    {children}
    {resizeColumn && (
      // eslint-disable-next-line jsx-a11y/role-supports-aria-props -- focusable separators expose their current value
      <span
        className={styles.Handle}
        role="separator"
        aria-orientation="vertical"
        aria-label={`تغییر عرض ستون ${resizeColumn.label}`.trim()}
        aria-valuemin={MIN_WIDTH}
        aria-valuemax={MAX_WIDTH}
        aria-valuenow={resizeColumn.width}
        tabIndex={0}
        title="برای تغییر عرض بکشید یا از کلیدهای چپ و راست استفاده کنید"
        onPointerDown={(event) => resizeColumn.start(event)}
        onKeyDown={(event) => resizeColumn.keyboard(event)}
        onClick={stopPropagation}
        onDoubleClick={stopPropagation}
        onMouseDown={stopPropagation}
      />
    )}
  </th>
));

const HeaderRow = ({ children, resizeLayout, ...props }) => {
  const rowRef = useRef(null);
  useLayoutEffect(() => {
    resizeLayout(rowRef.current.closest("thead"));
  }, [children, resizeLayout]);
  return <tr {...props} ref={rowRef}>{children}</tr>;
};

const ResizableTableContent = ({
  tableKey,
  columns = [],
  tableComponent: TableComponent = Table,
  components,
  scroll,
  tableLayout,
  ...props
}) => {
  const { direction } = useContext(ConfigProvider.ConfigContext);
  const [widths, setWidths] = useState(() => readWidths(tableKey));
  const widthsRef = useRef(widths);
  const containerRef = useRef(null);
  const dragCleanup = useRef(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const [visibleKeys, setVisibleKeys] = useState(null);
  const identifiedColumns = identifyColumns(columns);
  const hasWidths = Object.keys(widths).length > 0;

  const updateWidths = (nextWidths, persist = false) => {
    widthsRef.current = nextWidths;
    setWidths(nextWidths);
    if (persist) {
      try {
        localStorage.setItem(storageKey(tableKey), JSON.stringify(nextWidths));
      } catch {
        // Tables remain usable when browser storage is unavailable or full.
      }
    }
  };

  useEffect(() => () => dragCleanup.current?.(), []);

  const updateLayout = (header) => {
    if (!hasWidths) return;
    const body = containerRef.current?.querySelector(".ant-table-body");
    const gutter = body ? body.offsetWidth - body.clientWidth : 0;
    setScrollbarWidth((previous) => previous === gutter ? previous : gutter);
    // ProTable can hide/reorder columns through its own settings menu.
    const keys = JSON.stringify(Array.from(header.querySelectorAll("[data-resize-column]"), (cell) => cell.dataset.resizeColumn));
    setVisibleKeys((previous) => previous === keys ? previous : keys);
  };

  const measureColumns = (cell) => {
    const measured = { ...widthsRef.current };
    cell.closest("thead").querySelectorAll("[data-resize-column]").forEach((header) => {
      const width = header.getBoundingClientRect().width;
      if (validWidth(width)) measured[header.dataset.resizeColumn] = Math.round(width);
    });
    return measured;
  };

  const startResize = (event, columnKey) => {
    if (event.button !== 0 || event.isPrimary === false) return;
    event.preventDefault();
    event.stopPropagation();
    dragCleanup.current?.();
    const handle = event.currentTarget;
    const cell = handle.closest("th");
    const measured = measureColumns(cell);
    const startWidth = measured[columnKey] || 160;
    const startX = event.clientX;
    const pointerId = event.pointerId;
    const sign = (getComputedStyle(cell).direction || direction) === "rtl" ? -1 : 1;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    handle.setPointerCapture?.(pointerId);
    updateWidths(measured);

    const move = (moveEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      moveEvent.preventDefault();
      updateWidths({ ...measured, [columnKey]: clampWidth(startWidth + (moveEvent.clientX - startX) * sign) });
    };
    const cleanup = () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", finish);
      document.removeEventListener("pointercancel", finish);
      window.removeEventListener("blur", finish);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      if (handle.hasPointerCapture?.(pointerId)) handle.releasePointerCapture(pointerId);
      dragCleanup.current = null;
    };
    const finish = (endEvent) => {
      if (endEvent.type !== "blur" && endEvent.pointerId !== pointerId) return;
      cleanup();
      updateWidths(widthsRef.current, true);
    };
    dragCleanup.current = cleanup;
    document.addEventListener("pointermove", move, { passive: false });
    document.addEventListener("pointerup", finish);
    document.addEventListener("pointercancel", finish);
    window.addEventListener("blur", finish);
  };

  const resizeWithKeyboard = (event, columnKey) => {
    // Other keys must not reach a sortable header either.
    event.stopPropagation();
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const cell = event.currentTarget.closest("th");
    const measured = measureColumns(cell);
    const sign = (getComputedStyle(cell).direction || direction) === "rtl" ? -1 : 1;
    const delta = (event.key === "ArrowRight" ? 1 : -1) * sign * (event.shiftKey ? 50 : 10);
    updateWidths({ ...measured, [columnKey]: clampWidth((measured[columnKey] || 160) + delta) }, true);
  };

  let totalWidth = 0;
  const visibleColumns = visibleKeys === null ? null : new Set(JSON.parse(visibleKeys));
  const prepareColumns = (items) => items.map(({ columnKey, children, ...column }) => {
    if (children) return { ...column, children: prepareColumns(children) };
    const width = widths[columnKey] || (hasWidths ? (typeof column.width === "number" ? column.width : 160) : column.width);
    if (!column.hidden && !column.hideInTable && (!visibleColumns || visibleColumns.has(columnKey))) {
      totalWidth += typeof width === "number" ? width : 160;
    }
    return {
      ...column,
      width,
      onHeaderCell: (currentColumn) => ({
        ...column.onHeaderCell?.(currentColumn),
        resizeColumn: {
          key: columnKey,
          label: typeof column.title === "string" ? column.title : String(column.dataIndex || column.key || ""),
          headerLabel: typeof column.title === "string" ? column.title : undefined,
          width: typeof width === "number" ? width : 160,
          start: (event) => startResize(event, columnKey),
          keyboard: (event) => resizeWithKeyboard(event, columnKey),
        },
      }),
    };
  });
  const resizedColumns = prepareColumns(identifiedColumns);
  if (props.rowSelection) totalWidth += Number(props.rowSelection.columnWidth) || (props.bordered ? 48 : 32);
  if (props.expandable?.expandedRowRender && props.expandable.showExpandColumn !== false) {
    totalWidth += Number(props.expandable.columnWidth) || 48;
  }
  const withResizableHeader = (currentComponents) => ({
    ...currentComponents,
    header: { ...currentComponents?.header, cell: HeaderCell, row: HeaderRow },
  });
  // EditableProTable replaces `components` when it puts the create button in
  // the header. Merge at its final Table render to retain that button as well.
  const proTableProps = TableComponent !== Table ? {
    tableViewRender: (tableProps, defaultDom) => {
      const dom = cloneElement(defaultDom, { components: withResizableHeader(tableProps.components) });
      return props.tableViewRender ? props.tableViewRender(tableProps, dom) : dom;
    },
  } : {};

  return (
    <div
      ref={containerRef}
      dir={direction || "ltr"}
      className={[styles.Container, hasWidths && styles.Resized].filter(Boolean).join(" ")}
      style={{ "--resizable-header-width": hasWidths ? `${totalWidth + scrollbarWidth}px` : "auto" }}
    >
      <TableComponent
        {...props}
        {...proTableProps}
        columns={resizedColumns}
        components={withResizableHeader(components)}
        onHeaderRow={(currentColumns, index) => ({
          ...props.onHeaderRow?.(currentColumns, index),
          resizeLayout: updateLayout,
        })}
        scroll={hasWidths ? { ...scroll, x: totalWidth } : scroll}
        tableLayout={hasWidths ? "fixed" : tableLayout}
      />
    </div>
  );
};

// A key change starts with that table's saved settings, never the previous table's state.
const ResizableTable = (props) => <ResizableTableContent key={props.tableKey} {...props} />;

export default ResizableTable;
